import React, { useEffect, useState } from 'react';
import { Icon, Spinner, SpinnerSize, mergeStyleSets } from '@fluentui/react';
import { datasetService } from '../../services/datasetService';
import type { ColumnInfo, ColumnProfile, SampleValuesResult } from '../../types/dataset.types';

interface Props {
  datasetId: string;
  tableName: string;
  columns: ColumnInfo[];
  onClose: () => void;
}

const styles = mergeStyleSets({
  wrap: {
    marginTop: 12,
    borderRadius: 4,
    border: '1px solid #E1E1E1',
    background: '#FAFAFA',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderBottom: '1px solid #E1E1E1',
    background: '#F5F5F5',
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#232323',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#747474',
    cursor: 'pointer',
    fontSize: 14,
    padding: '4px 8px',
    borderRadius: 4,
    selectors: {
      ':hover': { background: '#F0F0F0', color: '#232323' },
    },
  },
  tableScroll: {
    overflowX: 'auto' as const,
  },
  sampleChip: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    background: '#F5F5F5',
    border: '1px solid #EDEDED',
    color: '#484848',
    marginRight: 4,
    marginBottom: 4,
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  loading: {
    padding: '8px 16px',
    fontSize: 11,
    color: '#747474',
  },
  profileCell: {
    fontSize: 11,
    color: '#484848',
    whiteSpace: 'nowrap' as const,
  },
  profileBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    background: '#F5F5F5',
    border: '1px solid #EDEDED',
    color: '#747474',
  },
});

export default function DataDictionary({ datasetId, tableName, columns, onClose }: Props) {
  const [samples, setSamples] = useState<Record<string, any[]> | null>(null);
  const [loadingSamples, setLoadingSamples] = useState(true);
  const [profiles, setProfiles] = useState<ColumnProfile[] | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingSamples(true);
    setLoadingProfiles(true);

    datasetService
      .getSampleValues(datasetId, tableName)
      .then((result: SampleValuesResult) => {
        if (!cancelled) setSamples(result.samples);
      })
      .catch(() => {
        if (!cancelled) setSamples(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSamples(false);
      });

    datasetService
      .getTableProfile(datasetId, tableName)
      .then((result) => {
        if (!cancelled) setProfiles(result.profiles);
      })
      .catch(() => {
        if (!cancelled) setProfiles(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingProfiles(false);
      });

    return () => { cancelled = true; };
  }, [datasetId, tableName]);

  const getProfile = (colName: string): ColumnProfile | undefined =>
    profiles?.find((p) => p.column_name === colName);

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          <Icon iconName="TextDocument" styles={{ root: { fontSize: 14 } }} />
          Data Dictionary: {tableName}
        </span>
        <button className={styles.closeBtn} onClick={onClose} title="Close">
          <Icon iconName="ChromeClose" />
        </button>
      </div>

      <div className={styles.tableScroll}>
        <div className="md-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Column Name</th>
                <th>Data Type</th>
                <th>Nullable</th>
                <th>Null %</th>
                <th>Distinct</th>
                <th>Min / Max</th>
                <th>Sample Values</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col) => {
                const profile = getProfile(col.name);
                return (
                  <tr key={col.name}>
                    <td style={{ fontWeight: 600 }}>{col.name}</td>
                    <td>
                      <code style={{
                        fontSize: 11,
                        background: '#d9f4d7',
                        padding: '2px 6px',
                        borderRadius: 4,
                        color: '#2e873d',
                      }}>
                        {col.dataType}
                      </code>
                    </td>
                    <td>{col.nullable ? 'Yes' : 'No'}</td>
                    {/* Null % */}
                    <td className={styles.profileCell}>
                      {loadingProfiles ? (
                        <Spinner size={SpinnerSize.xSmall} />
                      ) : profile ? (
                        <span className={styles.profileBadge}
                          style={profile.null_pct > 50 ? { background: 'rgba(216,59,1,0.08)', borderColor: 'rgba(216,59,1,0.2)', color: '#D83B01' } : undefined}
                        >
                          {profile.null_pct}%
                        </span>
                      ) : '-'}
                    </td>
                    {/* Distinct */}
                    <td className={styles.profileCell}>
                      {loadingProfiles ? (
                        <Spinner size={SpinnerSize.xSmall} />
                      ) : profile ? (
                        profile.distinct_count.toLocaleString()
                      ) : '-'}
                    </td>
                    {/* Min / Max */}
                    <td className={styles.profileCell}>
                      {loadingProfiles ? (
                        <Spinner size={SpinnerSize.xSmall} />
                      ) : profile && (profile.min_value !== null || profile.max_value !== null) ? (
                        <span>
                          {profile.min_value ?? '-'} / {profile.max_value ?? '-'}
                        </span>
                      ) : '-'}
                    </td>
                    {/* Sample Values */}
                    <td>
                      {loadingSamples ? (
                        <span className={styles.loading}>
                          <Spinner size={SpinnerSize.xSmall} />
                        </span>
                      ) : samples && samples[col.name] ? (
                        <div>
                          {samples[col.name].slice(0, 5).map((val, i) => (
                            <span key={i} className={styles.sampleChip}>
                              {val === null ? 'NULL' : String(val)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#747474', fontSize: 11 }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
